import { twMerge } from "tailwind-merge";
import { makeIntComma } from "common/hooks/utils/utils";
import { ReactComponent as Calendar } from "../../../assets/icons/calendar.svg";
import { ReactComponent as Marker } from "../../../assets/icons/marker.svg";
import { ReactComponent as CoryoLogoImg } from "../../../assets/icons/coryo_text_logo_white.svg";
import { useState } from "react";

type Props = {
    orderDate: string;
    status: string;
    productImg: string | null;
    productCategory: string;
    productName: string;
    deliveryDate: string;
    deliveryAddress: string;
    recipientName: string;
};
export default function OrderItem({
    orderDate,
    status,
    productImg,
    productCategory,
    productName,
    deliveryDate,
    deliveryAddress,
    recipientName,
}: Props) {
    return (
        <div className="py-[20px] px-[16px]">
            <div className="flex gap-[12px]">
                <figure className="border border-[#E5E5F1] rounded-[8px] w-[80px] h-[100px] overflow-hidden shrink-0">
                    {productImg ? (
                        <img src={productImg} alt={productName} className="w-full h-full object-cover" />
                    ) : (
                        <div className="bg-[#E5E5F1] border border-[#E5E5F1] w-full h-full flex items-center justify-center">
                            <CoryoLogoImg />
                        </div>
                    )}
                </figure>
                <div className="w-full overflow-hidden">
                    <div className="text-[14px] leading-[20px] max-w-full">
                        <span className="text-[#484964] font-bold">{status}</span>
                        <span className="text-[#999999] ml-[4px]">주문 {orderDate}</span>
                    </div>
                    <div className="py-[4px] max-w-full">
                        <p className="truncate">
                            [{productCategory}] {productName}
                        </p>
                    </div>
                    <div className="text-[14px] leading-[20px] text-[#484964]">
                        <div className="flex items-center gap-[5px] max-w-full">
                            <Calendar className="shrink-0" />
                            <p className="truncate">{deliveryDate}</p>
                        </div>
                        <div className="flex items-center gap-[5px] max-w-full">
                            <Marker className="shrink-0" />
                            <p className="truncate">
                                {recipientName} {deliveryAddress}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const SimpleOrderItem = ({
    className,
    size = "normal",
    category,
    name,
    price,
    quantity,
    totalPrice,
    img,
}: {
    className?: string;
    size?: "normal" | "large";
    category?: string;
    name?: string;
    price?: string | number;
    // 수량을 넘기면 단가 옆에 수량, 아래에 합계 금액을 함께 보여준다.
    quantity?: number;
    totalPrice?: number;
    img: string;
}) => {
    const style = twMerge("flex gap-[16px]", className);
    const [isHorizontal, setIsHorizontal] = useState<boolean | null>(null);

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        if (naturalWidth === naturalHeight) {
            setIsHorizontal(true);
            return;
        }
        setIsHorizontal(naturalWidth >= naturalHeight);
    };
 
    return (
        <div className={style}>
            <figure
                style={{ width: size === "normal" ? 72 : 100, height: size === "normal" ? 86 : 120 }}
                className="border border-[#E5E5F1] rounded-[8px] overflow-hidden shrink-0 flex justify-center items-center"
            >
                {img ? (
                    <img src={img} alt={name} onLoad={handleImageLoad} className={`object-cover ${isHorizontal ? "w-full" : "h-full"}`} />
                ) : (
                    <div className="bg-[#E5E5F1] border border-[#E5E5F1] w-full h-full flex items-center justify-center">
                        <CoryoLogoImg />
                    </div>
                )}
            </figure>
            <div className={`flex flex-col ${size === "normal" ? "py-[9px]" : "py-[18px] gap-[4px]"}`}>
                <p className="leading-[20px] tex-[14px] text-[#999] truncate mobile450:text-[12px]">{category}</p>
                  <p className={`${size === "normal" ? "leading-[24px]" : "text-[18px] leading-[28px]"} mobile450:!text-[16px] mobile450:!leading-[22px]`}>
                        {name?.split(",").map((part, i, arr) => (
                            <span key={i}>{part.trim()}{i < arr.length - 1 && <br />}</span>
                        ))}
                    </p>
                <p className={`${size === "normal" ? "leading-[24px]" : "text-[18px] leading-[28px]"} font-bold break-all mobile450:!text-[16px] mobile450:!leading-[22px]`}>
                    {price && makeIntComma(Number(price))}원
                    {quantity !== undefined && (
                        <span className="ml-[4px] font-[400] text-[14px] text-[#999] mobile450:text-[13px]">× {quantity}개</span>
                    )}
                </p>
                {quantity !== undefined && totalPrice !== undefined && (
                    <p className="leading-[20px] text-[14px] text-[#4B632B] mobile450:text-[13px]">
                        합계 <span className="font-bold">{makeIntComma(totalPrice)}원</span>
                    </p>
                )}
            </div>
        </div>
    );
};
